import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { Users, Clock, Laptop, Smartphone } from "lucide-react";

const LEVEL_KEY = {
  beginner: "course.beginner",
  intermediate: "course.intermediate",
  advanced: "course.advanced",
};

export default function CourseCard({ course }) {
  const { t, formatPrice, lang } = useI18n();
  const title = lang === "ro" && course.title_ro ? course.title_ro : course.title;

  const progress = Math.min(
    100,
    Math.round(((course.enrolled_count || 0) / Math.max(1, course.min_students)) * 100),
  );

  return (
    <article
      data-testid={`course-card-${course.slug}`}
      className="eduhu-card flex flex-col h-full"
    >
      <div className="p-7 pb-5 flex items-center justify-between text-xs">
        <span className="overline text-eduhu-inkSoft">{course.category}</span>
        <span
          className="px-2 py-1 border border-eduhu-border text-eduhu-dark"
          data-testid={`course-level-${course.slug}`}
        >
          {t(LEVEL_KEY[course.level] || "course.beginner")}
        </span>
      </div>

      <div className="px-7">
        <h3 className="font-serif text-2xl lg:text-[1.7rem] leading-tight text-eduhu-dark tracking-tight">
          {title}
        </h3>
        <p className="mt-3 text-sm text-eduhu-inkSoft leading-relaxed line-clamp-3">
          {course.summary}
        </p>
      </div>

      <div className="px-7 mt-5 flex flex-wrap gap-3 text-xs text-eduhu-inkSoft">
        <span className="inline-flex items-center gap-1.5">
          <Clock size={13} strokeWidth={1.5} />
          {course.continuous
            ? "Continuous"
            : `${course.weeks || 6} weeks · ${course.sessions_per_week || 3}×/wk`}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Users size={13} strokeWidth={1.5} /> {t("course.minStudents")}: {course.min_students}
        </span>
        <span className="inline-flex items-center gap-1.5">
          {course.requires_laptop ? (
            <Laptop size={13} strokeWidth={1.5} />
          ) : (
            <Smartphone size={13} strokeWidth={1.5} />
          )}
          {course.requires_laptop ? t("course.laptopRequired") : t("course.phoneAllowed")}
        </span>
      </div>

      <div className="px-7 mt-5">
        <div
          className="h-[2px] bg-eduhu-bg3 relative overflow-hidden"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${progress}% to next cohort`}
        >
          <div
            className="absolute inset-y-0 left-0 bg-eduhu-gold"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-xs text-eduhu-inkSoft">
          <span>{course.enrolled_count || 0} {t("course.enrolledLabel")}</span>
          <span>{progress}% {t("course.toCohort")}</span>
        </div>
      </div>

      <div className="mt-auto p-7 pt-5 flex items-end justify-between border-t border-eduhu-border">
        <div>
          <div className="overline text-eduhu-inkSoft">{t("course.from")}</div>
          <div className="font-serif text-3xl text-eduhu-dark">
            {formatPrice(course.price_usd)}
          </div>
        </div>
        <Link
          to={`/courses/${course.id}`}
          data-testid={`course-cta-${course.slug}`}
          aria-label={`View details for ${title}`}
          className="eduhu-btn-primary !py-2.5 !px-4 text-sm"
        >
          {t("course.viewDetails")}
        </Link>
      </div>
    </article>
  );
}
